import React, { useRef, useState } from 'react'
import { Download, Eye, FileText, Pen, Upload, X } from 'lucide-react'
import axios from '../../config/axios'
import AttachmentApiService, { GalleryFile } from '../../services/AttachmentApiService'
import AttachmentSourceModal from '../AttachmentSourceModal'
import AttachmentGalleryModal from '../AttachmentGalleryModal'
import { formatDate } from '../../utils/masks'
import openSignedAttachment from '../../utils/openSignedAttachment'
import { downloadRemoteFile } from '../../utils/downloadRemoteFile'
import {
  AttachmentList,
  CircularButton,
  CircularRemoveButton,
  DropZone,
  DropZoneText,
  HiddenInput,
  InlineExpInput,
  PrototypeCard,
  PrototypeIconBox,
  PrototypeMeta,
  PrototypeSubtitleRow,
  PrototypeTitleRow,
  SectionLabel,
  SectionWrapper,
  StatusBadge,
  TitleInput,
} from './style'

export interface AttachmentData {
  id?: number
  file?: File
  fileUrl?: string
  title: string
  isEditing: boolean
  expirationDate?: string
}

interface UserAttachmentSectionProps {
  attachments: AttachmentData[]
  setAttachments: React.Dispatch<React.SetStateAction<AttachmentData[]>>
  disabled?: boolean
}

function checkGeneralFileStatus(expDateStr?: string): 'vencido' | 'valido' | null {
  if (!expDateStr) return null
  const str = String(expDateStr).trim()
  if (!str) return null

  try {
    let dateObj: Date | null = null
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
      const [d, m, y] = str.split('/')
      dateObj = new Date(Number(y), Number(m) - 1, Number(d), 23, 59, 59)
    } else if (/^\d{2}\/\d{4}$/.test(str)) {
      const [m, y] = str.split('/')
      dateObj = new Date(Number(y), Number(m), 0, 23, 59, 59)
    } else if (str.includes('T') || str.includes('-')) {
      dateObj = new Date(str)
    }

    if (dateObj && !isNaN(dateObj.getTime())) {
      const now = new Date()
      return dateObj < now ? 'vencido' : 'valido'
    }
  } catch {
    return null
  }
  return null
}

const UserAttachmentSection: React.FC<UserAttachmentSectionProps> = ({
  attachments,
  setAttachments,
  disabled = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragActive, setIsDragActive] = useState(false)
  const [isSourceChoiceOpen, setIsSourceChoiceOpen] = useState(false)
  const [isGalleryOpen, setIsGalleryOpen] = useState(false)
  const [editingExpIndex, setEditingExpIndex] = useState<number | null>(null)

  const addFiles = (fileList: FileList | null) => {
    if (!fileList) return
    const newItems: AttachmentData[] = Array.from(fileList).map((file) => ({
      file,
      title: file.name,
      isEditing: false,
    }))
    setAttachments((prev) => [...prev, ...newItems])
  }

  const handleSelectGalleryFiles = async (galleryFiles: GalleryFile[]) => {
    const converted = await Promise.all(
      galleryFiles.map(async (gf) => {
        try {
          const signedUrl = gf.signedUrl || (await AttachmentApiService.getSignedUrl(gf.fileUrl))
          const res = await fetch(signedUrl)
          const blob = await res.blob()
          const file = new File([blob], gf.originalName || gf.fileName || gf.title || 'anexo', {
            type: gf.mimeType || blob.type,
          })
          return {
            file,
            fileUrl: gf.fileUrl,
            title: gf.title || gf.originalName || gf.fileName,
            isEditing: false,
          }
        } catch {
          return {
            fileUrl: gf.fileUrl,
            title: gf.title || gf.originalName || gf.fileName,
            isEditing: false,
          }
        }
      }),
    )
    setAttachments((prev) => [...prev, ...converted])
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragActive(false)
    if (!disabled) addFiles(e.dataTransfer.files)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    if (!disabled) setIsDragActive(true)
  }

  const handleDragLeave = () => setIsDragActive(false)

  const handleClick = () => {
    if (!disabled) setIsSourceChoiceOpen(true)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(e.target.files)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleRemove = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index))
  }

  const handleStartEditTitle = (index: number) => {
    if (disabled) return
    setAttachments((prev) =>
      prev.map((a, i) => (i === index ? { ...a, isEditing: true } : a)),
    )
  }

  const handleTitleChange = (index: number, value: string) => {
    setAttachments((prev) =>
      prev.map((a, i) => (i === index ? { ...a, title: value } : a)),
    )
  }

  const handleExpirationChange = (index: number, val: string) => {
    const formatted = formatDate(val)
    setAttachments((prev) =>
      prev.map((a, i) => (i === index ? { ...a, expirationDate: formatted } : a)),
    )
  }

  const handleSaveEditTitle = (index: number) => {
    setAttachments((prev) =>
      prev.map((a, i) => (i === index ? { ...a, isEditing: false } : a)),
    )
  }

  const handleView = async (index: number) => {
    const attachment = attachments[index]
    if (attachment.fileUrl) {
      await openSignedAttachment(attachment.fileUrl)
    } else if (attachment.file) {
      const url = URL.createObjectURL(attachment.file)
      window.open(url, '_blank')
    }
  }

  const handleDownload = async (index: number) => {
    const attachment = attachments[index]
    const fileName = attachment.title || 'anexo'

    if (attachment.fileUrl) {
      await downloadRemoteFile(attachment.fileUrl, fileName)
    } else if (attachment.file) {
      const objectUrl = URL.createObjectURL(attachment.file)
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = fileName
      link.click()
      URL.revokeObjectURL(objectUrl)
    }
  }

  return (
    <SectionWrapper>
      <SectionLabel>Anexos</SectionLabel>

      {attachments.length > 0 && (
        <AttachmentList>
          {attachments.map((attachment, index) => {
            const fileName = attachment.file?.name || attachment.title
            const expDate = attachment.expirationDate || ''
            const status = checkGeneralFileStatus(expDate)
            const isEditingExp = editingExpIndex === index

            return (
              <PrototypeCard key={index}>
                <PrototypeIconBox>
                  <FileText size={18} />
                </PrototypeIconBox>

                <PrototypeMeta>
                  <PrototypeTitleRow>
                    {attachment.isEditing ? (
                      <TitleInput
                        autoFocus
                        value={attachment.title}
                        onChange={(e) => handleTitleChange(index, e.target.value)}
                        onBlur={() => handleSaveEditTitle(index)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === 'Escape') handleSaveEditTitle(index)
                        }}
                      />
                    ) : (
                      <span
                        style={{ cursor: disabled ? 'default' : 'pointer' }}
                        onClick={() => handleStartEditTitle(index)}
                        title="Clique para editar o título"
                      >
                        {attachment.title}
                      </span>
                    )}
                  </PrototypeTitleRow>

                  <PrototypeSubtitleRow>
                    <span>{fileName}</span>
                    <span>·</span>
                    {isEditingExp ? (
                      <InlineExpInput
                        autoFocus
                        type="text"
                        placeholder="DD/MM/AAAA"
                        value={expDate}
                        onChange={(e) => handleExpirationChange(index, e.target.value)}
                        onBlur={() => setEditingExpIndex(null)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === 'Escape') setEditingExpIndex(null)
                        }}
                      />
                    ) : (
                      <span
                        style={{ cursor: disabled ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}
                        onClick={() => !disabled && setEditingExpIndex(index)}
                        title="Clique para alterar a validade"
                      >
                        {expDate ? `válido até ${expDate}` : 'sem validade (clique para definir)'}
                        {!disabled && <Pen size={10} color="#9ca3af" />}
                      </span>
                    )}
                  </PrototypeSubtitleRow>
                </PrototypeMeta>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {status === 'vencido' && <StatusBadge variant="vencido">VENCIDO</StatusBadge>}
                  {status === 'valido' && <StatusBadge variant="valido">VÁLIDO</StatusBadge>}

                  {(attachment.fileUrl || attachment.file) && (
                    <CircularButton
                      type="button"
                      onClick={() => handleView(index)}
                      title="Visualizar anexo"
                    >
                      <Eye size={13} />
                    </CircularButton>
                  )}

                  {(attachment.fileUrl || attachment.file) && (
                    <CircularButton
                      type="button"
                      onClick={() => handleDownload(index)}
                      title="Baixar anexo"
                    >
                      <Download size={13} />
                    </CircularButton>
                  )}

                  {!disabled && (
                    <CircularRemoveButton
                      type="button"
                      onClick={() => handleRemove(index)}
                      title="Remover anexo"
                    >
                      <X size={13} />
                    </CircularRemoveButton>
                  )}
                </div>
              </PrototypeCard>
            )
          })}
        </AttachmentList>
      )}

      <DropZone
        isDragActive={isDragActive}
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        style={disabled ? { opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none' } : undefined}
      >
        <Upload size={14} color="#6b7280" />
        <DropZoneText>
          Arraste arquivos ou clique para anexar — informe o tipo e a validade do documento
        </DropZoneText>
        <HiddenInput
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleChange}
        />
      </DropZone>

      <AttachmentSourceModal
        isOpen={isSourceChoiceOpen}
        onClose={() => setIsSourceChoiceOpen(false)}
        title="Adicionar Documento"
        description="Escolha como deseja adicionar os documentos do usuário:"
        onChooseUpload={() => {
          fileInputRef.current?.click()
        }}
        onChooseGallery={() => {
          setIsGalleryOpen(true)
        }}
      />

      <AttachmentGalleryModal
        isOpen={isGalleryOpen}
        onClose={() => setIsGalleryOpen(false)}
        multiple={true}
        title="Galeria da Empresa - Documentos"
        onSelect={(files: GalleryFile[]) => {
          handleSelectGalleryFiles(files)
        }}
      />
    </SectionWrapper>
  )
}

export default UserAttachmentSection
